import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { array, bool, func, number, object, string, oneOfType, shape } from 'prop-types';
import { Link } from 'react-router-dom';
import classnames from 'classnames';
import InPlaceIndicator from 'ps-components/LoadingIndicator/InPlaceIndicator/InPlaceIndicator';
import isEqual from 'lodash/isEqual';
import isIE from 'ps-components/Utils/checkForIE';
import Placeholder from '../Placeholder/Placeholder';
import Popover from 'ps-components/Popover/Popover';
import Search from 'ps-components/Search/Search';

import './ParticipationMatches.scss';
import { getSelectedSurveys } from '../helper';
import ParticipationMatchesGrid from './ParticipationMatchesGrid/ParticipationMatchesGrid';

function getCounts(jobs) {
  let counts = {
    data: 0,
    noData: 0
  };

  jobs?.forEach(row => {
    row.hasData ? counts.data++ : counts.noData++;
  });

  return counts;
}

ParticipationMatches.propTypes = {
  actions: object,
  addMatch: func,
  addSelectedCutID: func,
  benchmarkYear: number,
  currency: string,
  currentJob: shape({
    code: string,
    codeKey: string
  }),
  deleteMatch: func,
  dispatch: func,
  displayIn: string,
  error: string,
  getSurveyJobDetailsPageHref: func,
  grid: object,
  groupId: oneOfType([number, string]),
  handlePricingChange: func,
  hideSelect: bool,
  isAdHoc: bool,
  isGetJobPricingLoading: bool,
  isMatchDeleting: bool,
  jobId: oneOfType([number, string]),
  jobPricingGrid: object,
  loadingId: oneOfType([number, string]),
  location: object,
  masterAccountId: string,
  numericRounding: number,
  onBack: func,
  onDataCutClick: func,
  onSurveyJobTitleClick: func,
  organizationCode: string,
  percentRounding: number,
  removeSelectedCutID: func,
  renderPopover: bool,
  selectedRowId: number,
  selectedSurveys: array,
  selectedViewPk: number,
  showMinHeader: bool,
  surveyJobs: object,
  toggleHeader: func,
  togglePopover: func,
  updateModifiedDate: func,
  viewLoading: bool
};

export default function ParticipationMatches(props) {
  const [searchText, setSearchText] = useState('');
  const [showWithData, setShowWithData] = useState(true);
  const [loaded, setLoaded] = useState(false);
  const [selectedSurveys, setSelectedSurveys] = useState([]);
  const [totalFilteredDataCount, setTotalFilteredDataCount] = useState(0);
  const [totalFilteredNoDataCount, setTotalFilteredNoDataCount] = useState(0);
  const [showAboutMatches, setShowAboutMatches] = useState(false);

  const changeSearchValue = useRef();
  const aboutMatchesRef = useRef();

  useEffect(() => {
    if (props.render && !loaded && !props.grid.loading && !props.surveyJobs.jobs.loading) {
      loadPMData();
    }
  }, [props.render]);

  useEffect(() => {
    if (props.render && loaded && !props.grid.loading && !props.surveyJobs.jobs.loading) {
      loadSurveyJobData(props.surveyJobs.jobs);
    }
  }, [props.displayIn, props.selectedViewPk]);

  function loadPMData() {
    props.actions.resetParticipationMatches();
    setLoaded(false);
    props.handlePricingChange().then(() => {
      props.actions.getParticipationMatches(props.currency, props.currentJob, props.masterAccountId).then(matches => {
        loadSurveyJobData(matches);
      });
    });
  }

  function loadSurveyJobData(matches) {
    const options = {
      benchmarkYears: [props.benchmarkYear],
      displayIn: props.displayIn?.toLowerCase(),
      currency: props.currency,
      surveyViewName: undefined,
      data: matches
        ?.filter(j => j.hasData)
        ?.map(j => {
          return {
            cutId: j.cutId
          };
        })
    };

    if (options.data.length > 0) {
      props.actions.search(`/cms/api/accounts/${props.masterAccountId}/survey-jobs`, options, 'post').then(() => {
        setLoaded(true);
      });
    } else {
      setLoaded(true);
    }
  }

  useEffect(() => {
    setSearchText('');
  }, [props.selectedViewPk]);

  useEffect(() => {
    if (!props.render && !isEqual(props.selectedSurveys, selectedSurveys)) {
      setSelectedSurveys(props.selectedSurveys);
    }
  }, [props.selectedSurveys]);

  if (!props.render) {
    return <React.Fragment />;
  }

  // scrollRef used to scroll up to top after search
  let scrollRef = React.createRef();
  const counts = getCounts(props.surveyJobs?.jobs);

  function scrollAndSearch(searchText) {
    if (scrollRef?.current?.scrollTop) {
      scrollRef.current.scrollTop = 0; // reset page scroll
    }
    setSearchText(searchText);
  }

  // clicking x to clear search text
  function searchOnClick() {
    scrollAndSearch('');
  }

  // when pressing enter to execute a search
  function searchOnKeyDown(event) {
    if (event.key === 'Enter') {
      scrollAndSearch(event.target.value);
    }
  }

  // clicking the search icon to execute a search
  function searchOnSearch(value) {
    scrollAndSearch(value);
  }

  // Determine whether data is loading
  const dataLoading = props.surveyJobs?.loading || props.viewLoading;

  // Checks for no matches/recommendations
  const showNoMatchesPlaceholder = props.surveyJobs?.jobs?.length === 0 && props.surveyJobs?.loaded;
  const showNothing = props.surveyJobs?.jobs?.length === 0 && !props.surveyJobs?.loaded && !props.surveyJobs?.loading;

  const noMatchesPlaceholder = (
    <div className="participation-matches__no-results-container">
      <img className="no-results-img" src="/view/static/mp/images/market_place_matches.svg" />
      <Placeholder
        primaryText="Find Participation Matches"
        secondaryText="Suggestions are based on participation matches for this job"
        showText={true}
      />
      <div className="participation-matches__why">
        <div className="participation-matches__why-header">Why do I not see participation matches for this job?</div>
        <span className="participation-matches__why-body">
          There may be no matches to surveys in
          <a className="participation-matches__pcenter-link" href={getPCenterURL()} target="_blank">
            Survey Participation
          </a>
          for the last two years or the current pricing results already encompass all of your participation matches for
          this job.
        </span>
      </div>
    </div>
  );

  function toggleWithData(value) {
    setShowWithData(value);
    if (scrollRef?.current?.scrollTop) {
      scrollRef.current.scrollTop = 0; // reset pagination scroll
    }
  }

  function toggleAboutMatches(e) {
    e?.stopPropagation();
    setShowAboutMatches(!showAboutMatches);
  }

  function getPCenterURL() {
    return `${window.location.origin}/view/o/${props.organizationCode}/mp/iframe/merit/pcenter/pcenter`;
  }

  function getAboutMatchesText() {
    return (
      <React.Fragment>
        <div className="participation-matches__about-matches-title">What are Participation Matches?</div>
        <div className="participation-matches__about-matches-text">
          Participation matches are survey matches used for annual salary survey submissions and are stored in
          <a className="participation-matches__pcenter-link" href={getPCenterURL()} target="_blank">
            Survey Participation.
          </a>
        </div>

        <div className="participation-matches__about-matches-title">What does 'With Data' mean?</div>
        <div className="participation-matches__about-matches-text">
          Matches with data are ones you may want to add to your pricing results. Matches without data are participation
          matches not represented in recent survey data and are here for reference purposes.
        </div>
      </React.Fragment>
    );
  }

  function onScroll(e) {
    if (props.toggleHeader) {
      props.toggleHeader(e.target);
    }
  }

  return (
    <div className="participation-matches">
      <div className="participation-matches__content">
        {dataLoading || showNothing ? (
          <InPlaceIndicator />
        ) : showNoMatchesPlaceholder ? (
          noMatchesPlaceholder
        ) : (
          <React.Fragment>
            <div className="participation-matches__search-container">
              <Search
                className="participation-matches__search"
                onClick={searchOnClick}
                onKeyDown={searchOnKeyDown}
                onSearch={searchOnSearch}
                placeholder="Search Participation Matches"
                testId="participation-matches__search"
                useInternalState
                ref={changeSearchValue}
              />

              <div className={classnames('participation-matches__right-buttons', { ie: isIE() })}>
                <div className="participation-matches__links" ref={aboutMatchesRef}>
                  <Link
                    to="#"
                    id="about-matches-link"
                    className="participation-matches__link mpm-about-matches-link"
                    onClick={toggleAboutMatches}
                  >
                    About Participation Matches
                  </Link>
                  <div id="participation-matches__about-popover" className="participation-matches__about-popover"></div>
                </div>

                <React.Fragment>
                  {showAboutMatches &&
                    ReactDOM.createPortal(
                      <Popover
                        triggerElement={aboutMatchesRef}
                        relativeElement={document.querySelector('about-matches-link')}
                        display={showAboutMatches}
                        togglePopover={toggleAboutMatches}
                      >
                        {getAboutMatchesText()}
                      </Popover>,
                      document.getElementById('participation-matches__about-popover')
                    )}
                </React.Fragment>

                <div
                  className={classnames('participation-matches__toggle', 'left', { active: showWithData })}
                  onClick={() => toggleWithData(true)}
                >
                  {`With Data (${totalFilteredDataCount})`}
                </div>
                <div
                  className={classnames('participation-matches__toggle', 'right', { active: !showWithData })}
                  onClick={() => toggleWithData(false)}
                >
                  {`No Data (${totalFilteredNoDataCount})`}
                </div>
              </div>
            </div>

            <React.Fragment>
              <div
                ref={scrollRef}
                onScroll={onScroll}
                className={classnames('participation-matches__list-container', {
                  adhoc: props.isAdHoc,
                  min: props.showMinHeader,
                  unpurchased: !showWithData
                })}
              >
                <ParticipationMatchesGrid
                  actions={props.actions}
                  addMatch={props.addMatch}
                  addSelectedCutID={props.addSelectedCutID}
                  benchmarkYear={props.benchmarkYear}
                  counts={counts}
                  currency={props.currency}
                  deleteMatch={props.deleteMatch}
                  displayIn={props.displayIn}
                  filterText={searchText}
                  getSurveyJobDetailsPageHref={props.getSurveyJobDetailsPageHref}
                  grid={props.grid}
                  groupId={props.groupId}
                  hideSelect={props.hideSelect}
                  isMatchDeleting={props.isMatchDeleting}
                  jobId={props.jobId}
                  loadingId={props.loadingId}
                  masterAccountId={props.masterAccountId}
                  numericRounding={props.numericRounding}
                  onBack={props.onBack}
                  onDataCutClick={props.onDataCutClick}
                  onSurveyJobTitleClick={props.onSurveyJobTitleClick}
                  percentRounding={props.percentRounding}
                  removeSelectedCutID={props.removeSelectedCutID}
                  renderPopover={props.renderPopover}
                  selectedRowId={props.selectedRowId}
                  selectedSurveys={props.selectedSurveys}
                  selectedViewPk={props.selectedViewPk}
                  setTotalFilteredDataCount={setTotalFilteredDataCount}
                  setTotalFilteredNoDataCount={setTotalFilteredNoDataCount}
                  showWithData={showWithData}
                  surveyJobs={props.surveyJobs}
                  togglePopover={props.togglePopover}
                  updateModifiedDate={props.updateModifiedDate}
                />
              </div>
            </React.Fragment>
          </React.Fragment>
        )}
      </div>
    </div>
  );
}
